import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from 'utils/config';
import { formatDate, callApi, showSweetAlert } from 'utils/utils';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Row, Col } from 'reactstrap';
import FieldText from 'components/Controls/FieldText';
import FieldButton from 'components/Controls/FieldButton/FieldButton';
import FieldDropdown from 'components/Controls/FieldDropdown';
import FieldDataTable from 'components/Controls/FieldDataTable/FieldDataTable';
import FieldTextArea from 'components/Controls/FieldTextArea';
import FieldCalendar from 'components/Controls/FieldCalendar';
import * as yup from 'yup';
import YupValidator from 'utils/YupValidator.js';
import ITAssetsAssign from './ITAssetsAssign';
import ITAssetsDisable from './ITAssetsDisable';
import './ITAssets.scss'
import { Dialog } from 'primereact/dialog';

const ITAssetsDetails = (props) => {
  const {t, i18n} = useTranslation(['it', 'common']);

  const [assignmentHistory, setAssignmentHistory] = useState([]); // Asset assignment history
  const [asset, setAsset] = useState([]);
  const [edit, setEdit] = useState(false);
  const [equipmentList, setEquipmentList] = useState(null);
  const [brandList, setBrandList] = useState(null);
  const [errors, setErrors] = useState(null);
  const [equipment, setEquipment] = useState(null);
  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState(null);
  const [serialNumber, setSerialNumber] = useState(null);
  const [macAddress, setMacAddress] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [deviceNotes, setDeviceNotes] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const {selectedAsset, onHide} = props;

  useEffect(() => {
    getAssignmentHistory();
    getAssetDetails();
  }, [])

  const getAssignmentHistory = () => {
    const urlWebService = `${config.UrlApiProject}IT/GetITAssetAssignmentHistory?IdITEquipo=${selectedAsset.IdITEquipo}`;
    const paramsService = {};

    callApi(urlWebService, 'GET', paramsService, (response) => {
      setAssignmentHistory(response.data);
    })
  }

  const getAssetDetails = () => {
    const urlWebService = `${config.UrlApiProject}IT/GetITEquipmentDetails?IdITEquipo=${selectedAsset.IdITEquipo}`;
    const paramsService = {};

    callApi(urlWebService, 'GET', paramsService, (response) => {
      setAsset(response.data);
    })
  }

  const GetEquipments = () => {
    const urlWebService = `${config.UrlApiProject}IT/GetEquipmentTypes`;
    const paramsService = {};

    callApi(urlWebService, 'GET', paramsService, (response) => {
      setEquipmentList(response.data);
    });
  }

  const GetBrands = () => {
    const urlWebService = `${config.UrlApiProject}IT/GetEquipmentBrands`;
    const paramsService = {};

    callApi(urlWebService, 'GET', paramsService, (response) => {
      setBrandList(response.data);
    });
  }

  const assignedDateTemplate = (row) => {
    return (
      <>
        <span>{formatDate(row.FechaAsignada)}</span>
      </>
    )
  }

  const onEditClick = () => {
    GetEquipments();
    GetBrands();
    setEquipment(asset.ClaTipoEquipo);
    setBrand(asset.ClaMarcaEquipo);
    setModel(asset.ModeloEquipo);
    setSerialNumber(asset.SerialNumber);
    setMacAddress(asset.MacAddress);
    setVendor(asset.NomProveedor);
    setPurchaseOrder(asset.IdOrdenCompraRef);
    setDeviceNotes(asset.DeviceNotes);
    setPurchaseDate(new Date(asset.PurchaseDate));
    setEdit(true);
  }

  const onReassignClick = () => {
    setEdit(false);
    setShowDisable(false);
    setShowReassign(true);
  }

  const onHideReassign = () => {
    setShowReassign(false);
    getAssignmentHistory();
    getAssetDetails();
  }

  const onDisableClick = () => {
    setEdit(false);
    setShowReassign(false);
    setShowDisable(true);
  }

  const onHideDisable = () => {
    setShowDisable(false);
  }

  const onDisableAsset = () => {
    setShowDisable(false);
    onHide();
  }

  const onSaveClick = async () => {
    const validationScheme = yup.object().shape({
      equipment: yup.number().required('').nullable(),
      brand: yup.number().required('').nullable(),
      model: yup.string().required('').nullable(),
      serialNumber: yup.string().required('').nullable(),
      macAddress: yup.string().matches(/^(?:[A-F0-9]{2}([-:]))(?:[A-F0-9]{2}\1){4}[A-F0-9]{2}$/, {message: t('it:MACAddressError')}).nullable(),
      vendor: yup.string().nullable(),
      purchaseOrder: yup.string().nullable(),
      deviceNotes: yup.string().nullable(),
      purchaseDate: yup.date().nullable()
    });

    const value = {
      equipment,
      brand,
      model,
      serialNumber,
      macAddress,
      vendor,
      purchaseOrder,
      deviceNotes,
      purchaseDate
    }

    const validator = new YupValidator(validationScheme);
    const isValid = await validator.validate(value);

    if (!isValid) {
      setErrors(validator.errors);
    }
    else {
      const urlWebService = `${config.UrlApiProject}IT/UpdateITAssetDetails`;
      const paramsService = {
        IdITEquipo: asset.IdITEquipo,
        ClaTipoEquipo: equipment,
        ClaMarcaEquipo: brand,
        ModeloEquipo: model,
        SerialNumber: serialNumber,
        MacAddress: macAddress,
        NomProveedor: vendor,
        IdOrdenCompraRef: purchaseOrder,
        DeviceNotes: deviceNotes,
        FechaCompra: purchaseDate
      };
      callApi(urlWebService, 'POST', paramsService, (response) => {
        showSweetAlert("Success", t('it:AssetAdded'), 'success');
        getAssetDetails();
        setEdit(false);
      });
    }
  }

  const onCancelEditClick = () => {
    setEdit(false);
  }

  const onChangeEquipment = (e) => {
    const newErrors = {...errors};
    delete newErrors.equipment;
    setErrors(newErrors);
    setEquipment(e.target.value);
  }

  const onChangeBrand = (e) => {
    const newErrors = {...errors};
    delete newErrors.brand;
    setErrors(newErrors);
    setBrand(e.target.value);
  }

  const onChangeModel = (e) => {
    const newErrors = {...errors};
    delete newErrors.model;
    setErrors(newErrors);
    setModel(e.target.value);
  }

  const onChangeMacAddress = (e) => {
    const newErrors = {...errors};
    delete newErrors.macAddress;
    setErrors(newErrors);
    setMacAddress(e.target.value);
  }

  const onChangeSerialNumber = (e) => {
    const newErrors = {...errors};
    delete newErrors.serialNumber;
    setErrors(newErrors);
    setSerialNumber(e.target.value);
  }

  const onChangePurchaseOrder = (e) => {
    const newErrors = {...errors};
    delete newErrors.purchaseOrder;
    setErrors(newErrors);
    setPurchaseOrder(e.target.value === '' ? null : e.target.value);
  }

  const onChangePurchaseDate = (e) => {
    const newErrors = {...errors};
    delete newErrors.purchaseDate;
    setErrors(newErrors);
    setPurchaseDate(e.value);
  }

  const onChangeVendor = (e) => {
    const newErrors = {...errors};
    delete newErrors.vendor;
    setErrors(newErrors);
    setVendor(e.target.value === '' ? null : e.target.value);
  }

  const onChangeDeviceNotes = (e) => { 
    const newErrors = { ...errors } 
    delete newErrors.deviceNotes;   
    setErrors(newErrors)  
    setDeviceNotes(e.target.value)
  }

  const detailTable = (
    <table>
      <tr>
        <th>{t('it:ID')}</th>
        <td>{asset.IdITEquipo}</td>
      </tr>
      <tr>
        <th>{t('it:Equipment')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldDropdown
              name="equipment"
              options={equipmentList}
              value={equipment}
              optionValue='ClaTipoEquipo'
              optionLabel='NomTipoEquipo'
              onChange={onChangeEquipment}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.NomTipoEquipo}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:Brand')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldDropdown
              name="brand"
              options={brandList}
              value={brand}
              optionValue='ClaMarcaEquipo'
              optionLabel='NomMarcaEquipo'
              onChange={onChangeBrand}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.NomMarcaEquipo}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:Model')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldText
              name='model'
              value={model}
              onChange={onChangeModel}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.ModeloEquipo}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:SerialNumber')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldText
              name='serialNumber'
              value={serialNumber}
              onChange={onChangeSerialNumber}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.SerialNumber}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:MACAddress')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldText
              name='macAddress'
              value={macAddress}
              onChange={onChangeMacAddress}
              errors={errors}
              keyfilter={/[0-9A-F:-]/}
            />
          </td>
        ) : (
          <td>{asset.MacAddress}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:Vendor')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldText
              name="vendor"
              value={vendor}
              onChange={onChangeVendor}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.NomProveedor}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:PurchaseOrder')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldText
              name="purchaseOrder"
              value={purchaseOrder}
              onChange={onChangePurchaseOrder}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.IdOrdenCompraRef}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:Notes')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldTextArea
              name="deviceNotes"
              rows={3}
              placeholder=''
              maxLength={200}
              value={deviceNotes}
              onChange={onChangeDeviceNotes}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.DeviceNotes}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:PurchaseDate')}</th>
        { edit ? (
          <td className='p-fluid'>
            <FieldCalendar
              name='purchaseDate'
              value={purchaseDate}
              onChange={onChangePurchaseDate}
              errors={errors}
            />
          </td>
        ) : (
          <td>{asset.PurchaseDate}</td>
        )}
      </tr>
      <tr>
        <th>{t('it:AddedDate')}</th>
        <td>{asset.AddedDate}</td>
      </tr>
      <tr>
        <th>{t('it:AssignedDate')}</th>
        <td>{asset.AssignedDate}</td>
      </tr>
      <tr>
        <th>{t('it:Location')}</th>
        <td>{asset.NomUbicacion}</td>
      </tr>
      <tr>
        <th>{t('it:Building')}</th>
        <td>{asset.Building}</td>
      </tr>
      <tr>
        <th>{t('it:AssignedTo')}</th>
        <td>{asset.AssignedTo}</td>
      </tr>
      <tr>
        <th>{t('it:AssignedUserWTool')}</th>
        <td>{asset.AssignedUser}</td>
      </tr>
      <tr>
        <th>{t('it:AssignedEmployeeADP')}</th>
        <td>{asset.AssignedEmployee}</td>
      </tr>
      <tr>
        <th>{t('it:Email')}</th>
        <td>{asset.Email}</td>
      </tr>
    </table>
  )

  return (
    <>
      <Row>
        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className='mb-2 mt-1 p-d-flex justify-content-start'>
            { edit ? (
              <>
                <FieldButton label={t('common:Save')} icon='pi pi-save' className='p-button p-button-success mr-2' onClick={onSaveClick} />
                <FieldButton label={t('common:Cancel')} icon='pi pi-times' className='p-button p-button-danger' onClick={onCancelEditClick} />
              </>
            ) : (
              <>
                <FieldButton label={t('common:Edit')} icon='pi pi-pencil' className='mr-2' onClick={onEditClick} />
                <FieldButton label={t('it:Reassign')} icon='pi pi-user' className='p-button p-button-warning mr-2' onClick={onReassignClick} />
                <FieldButton label={t('it:Disable')} icon='pi pi-trash' className='p-button p-button-danger' onClick={onDisableClick} />
              </>
            )}
          </div>
        </Col>
      </Row>
      <Row className='p-d-flex align-items-start'>
        <Col xs={12} sm={12} md={12} lg={12} xl={6}>
          <div className='it-asset-detail-table mb-2'>
            {detailTable}
          </div>
        </Col>
        <Col xs={12} sm={12} md={12} lg={12} xl={6}>
          <FieldDataTable
            value={assignmentHistory}
            header={t('it:AssignmentHistory')}
            paginator={false}
          >
            <Column body={assignedDateTemplate} header={t('common:Date')} headerStyle={{ width: '90px' }} />
            <Column field='NombreUbicacion' header={t('it:Location')} headerStyle={{ width: '150px' }} />
            <Column field='AssignedTo' header={t('it:AssignedTo')} headerStyle={{ width: '150px' }} />
            <Column field='AdpIdName' header={t('it:Employee')} headerStyle={{ width: '150px' }} />
            <Column field='Notas' header={t('it:Notes')} />
          </FieldDataTable>
        </Col>
      </Row>
      <Dialog
        visible={showReassign}
        modal
        onHide={() => onHideReassign()}
        header={t('it:Reassign')}
        style={{ width: '50vw' }}
        breakpoints={{ '1000px': '75vw', '500px': '100vw' }}
        blockScroll={true}
      >
        <ITAssetsAssign selectedAsset={selectedAsset} onHide={onHideReassign} />
      </Dialog>
      <Dialog
        visible={showDisable}
        onHide={() => onHideDisable()}
        header={t('it:Disable')}
        style={{ width: '50vw' }}
        breakpoints={{ '1700px': '75vw', '1000px': '100vw' }}
        blockScroll={true}
      >
        <ITAssetsDisable selectedAsset={selectedAsset} onHide={onDisableAsset} />
      </Dialog>
    </>
  )
}

export default ITAssetsDetails;